import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { productEntryService, ProductEntrySession } from '../services/productEntryService';

interface ProductEntryContextType {
  // Current active session
  activeSession: ProductEntrySession | null;
  selectedCategory: 'jewelry' | 'watches' | null;
  
  // Session management
  createSession: (category: 'jewelry' | 'watches', sku: string) => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  updateSession: (updates: Partial<ProductEntrySession>) => Promise<void>;
  resetSession: () => void;
  
  // Session state
  isCreatingSession: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Session persistence
  saveSessionToStorage: () => void;
  loadSessionFromStorage: () => void;
  clearSessionFromStorage: () => void;
}

const ProductEntryContext = createContext<ProductEntryContextType | undefined>(undefined);

interface ProductEntryProviderProps {
  children: ReactNode;
}

export const ProductEntryProvider: React.FC<ProductEntryProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  
  // Session state
  const [activeSession, setActiveSession] = useState<ProductEntrySession | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'jewelry' | 'watches' | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear session on mount (don't persist across page reloads)
  useEffect(() => {
    if (currentUser) {
      clearSessionFromStorage();
      setActiveSession(null);
      setSelectedCategory(null);
    }
  }, [currentUser]);

  // Create new session
  const createSession = async (category: 'jewelry' | 'watches', sku: string) => {
    if (!currentUser) {
      setError('You must be logged in to create product entries');
      return;
    }

    if (!sku.trim()) {
      setError('SKU is required to create a product entry session');
      return;
    }

    setIsCreatingSession(true);
    setError(null);
    
    try {
      // Set user ID in service
      productEntryService.setUserId(currentUser.uid);
      
      console.log('🔍 DEBUG: Creating session with:', { 
        category, 
        sku: sku.trim(), 
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userRoles: currentUser.roles,
        userRolesString: JSON.stringify(currentUser.roles)
      });
      
      const createdSession = await productEntryService.createProductEntrySession(
        category, 
        sku.trim(),
        currentUser.displayName || currentUser.email || 'Unknown User'
      );
      
      console.log('Session created:', createdSession);
      
      // Set the session directly instead of loading it
      setActiveSession(createdSession);
      setSelectedCategory(category);
      
      console.log('Session loaded successfully');
      
      // Start activity tracking
      await productEntryService.startActivitySession(createdSession.sessionId);
      
      console.log('Activity tracking started');
    } catch (error: any) {
      console.error('Error creating product entry session:', error);
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      });
      setError(`Failed to start product entry session: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Load existing session
  const loadSession = async (sessionId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const session = await productEntryService.getProductEntrySession(sessionId);
      if (session) {
        setActiveSession(session);
        setSelectedCategory(session.category);
      } else {
        setError('Session not found');
      }
    } catch (error) {
      console.error('Error loading session:', error);
      setError('Failed to load session');
    } finally {
      setIsLoading(false);
    }
  };

  // Update session
  const updateSession = async (updates: Partial<ProductEntrySession>) => {
    if (!activeSession) return;

    try {
      await productEntryService.updateProductEntrySession(activeSession.sessionId, updates);
      
      // Update local state
      setActiveSession(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      console.error('Error updating session:', error);
      setError('Failed to update session');
    }
  };

  // Reset session
  const resetSession = async () => {
    // End activity tracking before resetting
    if (activeSession) {
      try {
        const activitySessionId = localStorage.getItem(`activity_session_${activeSession.sessionId}`);
        if (activitySessionId) {
          await productEntryService.endActivitySession(activeSession.sessionId, activitySessionId);
          localStorage.removeItem(`activity_session_${activeSession.sessionId}`);
          console.log('Activity session ended:', activitySessionId);
        }
      } catch (error) {
        console.error('Error ending activity session:', error);
      }
    }
    
    setActiveSession(null);
    setSelectedCategory(null);
    setError(null);
    clearSessionFromStorage();
  };

  // Save session to localStorage
  const saveSessionToStorage = () => {
    if (activeSession) {
      const sessionData = {
        sessionId: activeSession.sessionId,
        category: selectedCategory,
        timestamp: Date.now()
      };
      localStorage.setItem('productEntrySession', JSON.stringify(sessionData));
    }
  };

  // Load session from localStorage
  const loadSessionFromStorage = async () => {
    try {
      const stored = localStorage.getItem('productEntrySession');
      if (stored) {
        const sessionData = JSON.parse(stored);
        
        // Check if session is not too old (24 hours)
        const isRecent = Date.now() - sessionData.timestamp < 24 * 60 * 60 * 1000;
        if (isRecent) {
          await loadSession(sessionData.sessionId);
        } else {
          // Clear old session
          clearSessionFromStorage();
        }
      }
    } catch (error) {
      console.error('Error loading session from storage:', error);
      clearSessionFromStorage();
    }
  };

  // Clear session from localStorage
  const clearSessionFromStorage = () => {
    localStorage.removeItem('productEntrySession');
  };

  const value: ProductEntryContextType = {
    activeSession,
    selectedCategory,
    createSession,
    loadSession,
    updateSession,
    resetSession,
    isCreatingSession,
    isLoading,
    error,
    saveSessionToStorage,
    loadSessionFromStorage,
    clearSessionFromStorage
  };

  return (
    <ProductEntryContext.Provider value={value}>
      {children}
    </ProductEntryContext.Provider>
  );
};

export const useProductEntry = (): ProductEntryContextType => {
  const context = useContext(ProductEntryContext);
  if (context === undefined) {
    // Return a no-op fallback so components that optionally use this context
    // (e.g. InventoryDatabaseTable) don't crash outside the provider (HMR/dev).
    return {
      activeSession: null,
      selectedCategory: null,
      createSession: async () => {},
      resetSession: () => {},
      isCreatingSession: false,
      loadSession: async () => {},
    } as unknown as ProductEntryContextType;
  }
  return context;
};
